import { useCallback, useEffect, useRef, useState } from "react";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

// Tuning constants for the scroll-linked scrub loop.
const LERP_FACTOR = 0.16; // how quickly currentTime eases toward the target
const SETTLE_EPSILON = 0.0015; // snap to target when this close
const SEEK_EPSILON = 0.01; // avoid redundant seeks smaller than this
const END_GUARD = 0.05; // keep off the very last frame to avoid flicker

const logDev = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn("[useScrollVideo]", ...args);
  }
};

const READY_EVENTS = ["loadedmetadata", "loadeddata", "canplay"];

/**
 * useScrollVideo
 * Drives a <video> element's currentTime from the scroll position of a track element.
 * - Uses window scroll (no nested scroll containers).
 * - requestAnimationFrame + lerp smoothing to avoid janky seeking.
 * - Exposes `progress` (0..1) for synchronized overlay text.
 *
 * When `enabled` is false (reduced motion / unsupported), it still reports
 * scroll progress so overlay text can appear, but it does not scrub the video.
 */
export function useScrollVideo({ videoRef, trackRef, enabled = true }) {
  const rafRef = useRef(null);
  const currentTimeRef = useRef(0);
  const targetTimeRef = useRef(0);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);

  // --- Detect metadata / duration readiness ---
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const markReady = () => {
      if (video.duration && !Number.isNaN(video.duration)) setReady(true);
    };
    if (video.readyState >= 1 && video.duration) markReady();
    READY_EVENTS.forEach((evt) => video.addEventListener(evt, markReady));
    return () => READY_EVENTS.forEach((evt) => video.removeEventListener(evt, markReady));
  }, [videoRef]);

  // --- Prime the video for mobile Safari ---
  // iOS won't paint frames or honor currentTime seeks on a video that has
  // never played. Kick it with a muted play()→pause() as soon as it's ready;
  // if that's blocked (e.g. Low Power Mode), retry on the first touch.
  useEffect(() => {
    if (!enabled || !ready) return undefined;
    const video = videoRef.current;
    if (!video) return undefined;
    let cancelled = false;
    let removeGestureRetry = () => {};

    const prime = () => {
      // data-saver mode: don't force the download just to prime frames
      if (video.getAttribute("preload") === "none") return;
      const attempt = video.play();
      if (!attempt || !attempt.then) return;
      attempt
        .then(() => {
          if (cancelled) return;
          video.pause();
          // Nudge off frame zero so the primed frame actually renders.
          if (video.currentTime === 0) video.currentTime = 0.001;
        })
        .catch(() => {
          if (cancelled) return;
          const retry = () => {
            removeGestureRetry();
            prime();
          };
          window.addEventListener("touchstart", retry, { passive: true });
          window.addEventListener("pointerdown", retry, { passive: true });
          removeGestureRetry = () => {
            window.removeEventListener("touchstart", retry);
            window.removeEventListener("pointerdown", retry);
          };
        });
    };

    prime();
    return () => {
      cancelled = true;
      removeGestureRetry();
    };
  }, [enabled, ready, videoRef]);

  // --- Compute scroll progress (0..1) across the hero track ---
  const calculateProgress = useCallback(() => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    const total = track.offsetHeight - window.innerHeight;
    const scrolled = clamp(-rect.top, 0, Math.max(total, 1));
    return total > 0 ? scrolled / total : 0;
  }, [trackRef]);

  // --- Scroll/resize handler: update progress + target playback time ---
  const handleScroll = useCallback(() => {
    const p = calculateProgress();
    setProgress(p);
    const video = videoRef.current;
    if (video && video.duration) {
      targetTimeRef.current = p * (video.duration - END_GUARD);
    }
  }, [calculateProgress, videoRef]);

  // --- Advance the video one lerp step toward the target time ---
  const updateVideoFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    const cur = currentTimeRef.current;
    const tgt = targetTimeRef.current;
    const diff = tgt - cur;
    const next = Math.abs(diff) < SETTLE_EPSILON ? tgt : cur + diff * LERP_FACTOR;
    currentTimeRef.current = next;
    try {
      const t = clamp(next, 0, video.duration - END_GUARD);
      if (Math.abs(video.currentTime - t) > SEEK_EPSILON) {
        video.currentTime = t;
      }
    } catch (err) {
      // Some browsers throw transiently while a previous seek is still in flight.
      logDev("seek skipped", err);
    }
  }, [videoRef]);

  // --- Wire listeners + rAF scrubbing loop ---
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const loop = () => {
      updateVideoFrame();
      rafRef.current = requestAnimationFrame(loop);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);

    const video = videoRef.current;
    if (enabled && video) {
      try {
        video.pause();
      } catch (err) {
        logDev("pause failed", err);
      }
      rafRef.current = requestAnimationFrame(loop);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled, ready, trackRef, videoRef, handleScroll, updateVideoFrame]);

  return { progress, ready };
}

export default useScrollVideo;
